import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErrorCode,
  type DuplicateContactFlagListDto,
  type ContactMergeRequest,
  type ContactMergeResult,
  isStaleWrite,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { CLIENT_CONFLICT_MARKER } from '../../lib/clientConflict';
import { writeContactVersion } from './useFinanceContacts';

// Duplicate-donor resolution — the treasurer-facing data path for the inline
// ledger resolve panel. A duplicate only matters at filing (it understates a
// donor's totals / limit math), so the panel lets the treasurer Merge two split
// records into one or Dismiss the pair as genuinely different BEFORE filing.
//
// Backend (already built): GET the list, POST /merge (200 always — discriminate
// on `outcome`), POST /{id}/dismiss (204). Multi-tenancy: the shared
// `authenticatedFetch` interceptor adds the X-Subsidiary + X-Campaign-Id
// headers; every key is cid()-scoped so cross-campaign cache stays isolated.

/**
 * Every Open duplicate-contact flag in the campaign. `staleTime: 30_000` keeps a
 * warm close↔elsewhere navigation from re-downloading within 30 s (mirrors the
 * sort-inbox display hook); a merge / dismiss invalidates it explicitly.
 */
export function useDuplicateContactFlags() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.duplicateContactFlags(),
    queryFn: () =>
      apiJson<DuplicateContactFlagListDto>('/api/finance/duplicate-contact-flags', {
        error: 'Failed to fetch duplicate-contact flags',
      }),
    staleTime: 30_000,
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// The invalidation shared by merge + dismiss. Both remove a flag (the pair is
// resolved), which changes: the flag list (panel + row badge), the sort-inbox
// display + queue (the merged/dismissed contact's rows re-render under the survivor),
// the contacts list (a loser contact disappears; the survivor's aggregates shift),
// and the dashboard duplicate count. Invalidating every finance query covers all of them.
function invalidateResolved(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries(financeQueries);
}

/**
 * POST /merge. The endpoint always returns 200 with a discriminated
 * `ContactMergeResult` (`Merged` / `NeedsAmendmentConfirmation` / `Blocked`) —
 * so only a `Merged` outcome invalidates. The non-terminal outcomes leave the
 * flag Open (the treasurer confirms an amendment, or resolves a block manually),
 * so refreshing then would just re-render the same panel.
 */
export function useMergeContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    // Only the coded stale-view 409 becomes the refresh prompt; another 409 (e.g. the
    // legal-acceptance gate, whose `error` is a machine code) surfaces its readable message.
    mutationFn: (data: ContactMergeRequest) =>
      apiJson<ContactMergeResult>('/api/finance/duplicate-contact-flags/merge', {
        method: 'POST',
        json: data,
        error: (body, response) => {
          if (isStaleWrite(body)) return new Error(CLIENT_CONFLICT_MARKER);
          const text = response.status === 409 ? body.message || body.error : body.error;
          return new Error(text || 'Failed to merge contacts');
        },
      }),
    onSuccess: (result) => {
      if (result.outcome === 'Merged') {
        if (result.survivorConcurrencyVersion != null) {
          writeContactVersion(queryClient, result.survivorContactId, result.survivorConcurrencyVersion);
        }
        invalidateResolved(queryClient);
      }
    },
  });
}

/**
 * POST /{id}/dismiss — marks the pair genuinely different (204, no payload). The
 * flag leaves the Open set, so it invalidates the same surfaces as a merge.
 */
export function useDismissDuplicateFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    // 204 No Content — no payload.
    mutationFn: (flagId: string) =>
      apiVoid(`/api/finance/duplicate-contact-flags/${flagId}/dismiss`, {
        method: 'POST',
        error: (error) => new Error(error.error || 'Failed to dismiss duplicate flag'),
      }),
    onSuccess: () => {
      invalidateResolved(queryClient);
    },
  });
}
